import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useAuth, Ctx } from './context/auth';
import { useTheme } from './context/theme';
import { useToast } from './context/toast';
import { useConfirm, ConfirmProvider } from './context/confirm';
import { api } from './utils/api';
import { useIsMobile, useAutoRefresh } from './utils/hooks';
import { renderIcon } from './utils/icons';
import { N, R, STS } from './utils/constants';
import { parseRoute } from './utils/routing';
import { Btn, Inp, Sel, Card, Logo, Avatar, EmptyState, PR } from './components/ui';
import { Dots } from './components/ui/Skeletons';
import { AutoRefreshIndicator } from './components/ui/AutoRefreshIndicator';
import { NotificationBell } from './components/layout/NotificationBell';
import { BottomNav } from './components/layout/BottomNav';
import { PageTransition } from './components/layout/PageTransition';
import { Login } from './components/Login';
import Onboard from './components/Onboard';
import { Chat } from './components/Chat';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import UpdateToast from './components/UpdateToast';
import CmdPalette from './components/CmdPalette';
import SiteProgressChart from './components/SiteProgressChart';
import TasksDueSoon from './components/TasksDueSoon';
import TeamWorkload from './components/TeamWorkload';
import DashboardView from './pages/Dashboard/DashboardView';
import SiteDetailView from './pages/SiteDetail/SiteDetailView';

// Lazy-loaded pages for code splitting
const PipelineView = lazy(() => import('./pages/Pipeline'));
const AdminPortal = lazy(() => import('./pages/Admin'));
const AdminGuide = lazy(() => import('./pages/Admin/AdminGuide'));
const MemberGuide = lazy(() => import('./pages/Admin/MemberGuide'));
const CalendarView = lazy(() => import('./pages/Calendar/CalendarView'));
const GmailPanel = lazy(() => import('./pages/Email/GmailPanel'));
const MeetPanel = lazy(() => import('./pages/Meet/MeetPanel'));
const AnalyticsDashboard = lazy(() => import('./pages/Analytics/AnalyticsDashboard'));
const PartnersView = lazy(() => import('./pages/Partners/PartnersView'));
const AccountingView = lazy(() => import('./pages/Accounting/AccountingView'));
const DocumentsBrowser = lazy(() => import('./pages/Documents/DocumentsBrowser'));
const ActivityLog = lazy(() => import('./pages/Activity/ActivityLog'));
const SiteMissionControl = lazy(() => import('./pages/Dashboard/SiteMissionControl'));
const PipelineAnalytics = lazy(() => import('./pages/Analytics/PipelineAnalytics'));


const SuspenseFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Dots /></div>
);

export default function App() {
  const { user, setUser, login, logout, loading, sessionExpired, setSessionExpired } = useAuth();
  const { theme, isDark, toggle: toggleTheme } = useTheme();
  const { toast } = useToast();
  const showConfirm = useConfirm();
  const mobile = useIsMobile();

  // Core state
  const [view, setView] = useState(() => parseRoute().view);
  const [sb, setSb] = useState(() => typeof window !== 'undefined' ? window.innerWidth >= 768 : true);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [selSite, setSelSite] = useState(() => parseRoute().siteId || null);
  const [siteData, setSiteData] = useState(null);
  const [expWS, setExpWS] = useState(new Set([0]));
  const [selItems, setSelItems] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [taskStatusFilter, setTaskStatusFilter] = useState('');
  const [taskPriorityFilter, setTaskPriorityFilter] = useState('');
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [partners, setPartners] = useState([]);
  const [siteViewMode, setSiteViewMode] = useState(() => {
    const r = parseRoute();
    if (r.viewMode) return r.viewMode;
    return localStorage.getItem('dlp-site-view') || 'workflow';
  });
  const [wfStage, setWfStage] = useState(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [selectedPhases, setSelectedPhases] = useState(new Set());
  const searchInputRef = useRef(null);
  const searchTimer = useRef(null);
  const [searchIdx, setSearchIdx] = useState(-1);
  const pageViewRef = useRef({ page: null, ts: 0 });

  const T = theme;

  // Page view tracking (debounced — skip repeat views within 30s)
  useEffect(() => {
    if (!user) return;
    const page = selSite ? 'sites/' + selSite : view;
    const now = Date.now();
    if (pageViewRef.current.page === page && now - pageViewRef.current.ts < 30000) return;
    pageViewRef.current = { page, ts: now };
    const siteName = selSite ? (sites.find(s => s.id === selSite)?.name || '') : '';
    api.post('/api/track/page-view', { page: selSite ? 'site_detail' : view, site_id: selSite || undefined, site_name: siteName || undefined }).catch(() => {});
  }, [view, selSite]);

  // Load partners
  useEffect(() => {
    if (user) api.get('/api/partners').then(d => setPartners(d.partners || [])).catch(() => {});
  }, [user]);

  // URL routing
  useEffect(() => {
    window.history.replaceState({ view, siteId: selSite }, '', window.location.pathname);
    const onPop = (e) => {
      if (e.state) {
        if (e.state.view) setView(e.state.view);
        if (e.state.siteId) setSelSite(e.state.siteId);
        else setSelSite(null);
      } else {
        const r = parseRoute();
        setView(r.view);
        if (r.siteId) setSelSite(r.siteId);
        else setSelSite(null);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Google OAuth callback
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const g = p.get('google');
    if (g === 'connected') {
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => {
        alert('Google account connected successfully! Gmail, Calendar, Drive, and Meet are now available.');
        window.location.reload();
      }, 200);
    } else if (g === 'error') {
      const reason = p.get('reason') || 'Unknown error';
      window.history.replaceState({}, '', '/');
      alert('Google connection failed: ' + reason + '\n\nPlease try again.');
    }
  }, []);

  // Auto-hide sidebar on mobile
  useEffect(() => { if (mobile) setSb(false); }, [mobile]);

  // Dashboard refresh
  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const [st, si, u, a, ov] = await Promise.all([
        api.get('/api/stats'),
        api.get('/api/sites'),
        api.get('/api/users'),
        api.get('/api/activity?limit=20'),
        api.get('/api/stats/overdue'),
      ]);
      setStats(st);
      setSites(si.sites);
      setUsers(u.users);
      setActivity(a.activity);
      setOverdueCount(ov.count || 0);
      return { st, si, u, a, ov };
    } catch (e) {
      console.error(e);
      throw e;
    }
  }, [user]);

  const dashboardAR = useAutoRefresh(refresh, 30000, { enabled: !!user, key: 'dashboard' });

  // Unread polling
  const fetchUnread = useCallback(async () => {
    if (!user) return;
    const d = await api.get('/api/chat/general/unread?since=' + new Date(Date.now() - 3600000).toISOString());
    setUnread(d.count);
    return d;
  }, [user]);
  useAutoRefresh(fetchUnread, chatOpen ? 10000 : 60000, { enabled: !!user, key: 'unread' });

  // Site loading
  const loadSite = async (id) => {
    try {
      const d = await api.get(`/api/sites/${id}`);
      setSiteData(d);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    window.__dlpRefresh = () => { if (selSite) loadSite(selSite); };
    return () => { delete window.__dlpRefresh; };
  }, [selSite]);

  useEffect(() => { if (selSite) { loadSite(selSite); setSelectedPhases(new Set()); } }, [selSite]);

  // Favicon badge for unread/overdue count
  useEffect(() => {
    const count = (unread || 0) + (overdueCount || 0);
    const link = document.querySelector("link[rel~='icon']");
    if (!link) return;
    if (!count) { link.href = '/favicon.svg'; return; }
    const canvas = document.createElement('canvas');
    canvas.width = 64; canvas.height = 64;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, 64, 64);
      ctx.fillStyle = '#EF4444';
      ctx.beginPath(); ctx.arc(50, 14, 14, 0, 2 * Math.PI); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.font = 'bold 18px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(count > 99 ? '99+' : String(count), 50, 15);
      link.href = canvas.toDataURL('image/png');
    };
    img.src = '/favicon.svg';
  }, [unread, overdueCount]);

  const updateItem = async (id, ch) => {
    // Optimistic: update local state immediately
    const prev = siteData;
    if (siteData?.workstreams) {
      setSiteData(sd => ({
        ...sd,
        workstreams: sd.workstreams.map(ws => ({
          ...ws,
          items: (ws.items || []).map(it => it.id === id ? { ...it, ...ch } : it),
        })),
      }));
    }
    try {
      await api.put(`/api/items/${id}`, ch);
      // Background sync — no full reload needed for status/assignment changes
      if (!('status' in ch) && !('assigned_to' in ch)) {
        if (selSite) loadSite(selSite);
      }
      refresh();
    } catch (e) {
      toast(e.message, 'error');
      if (prev) setSiteData(prev); // rollback
    }
  };

  const deleteItem = async (id) => {
    try {
      await api.del(`/api/items/${id}`);
      toast('Item deleted', 'success');
      if (selSite) loadSite(selSite);
      refresh();
    } catch (e) { toast(e.message, 'error'); }
  };

  const addNote = async (sid, txt) => {
    try {
      await api.post(`/api/sites/${sid}/notes`, { text: txt });
      toast('Note added', 'success');
      if (selSite) loadSite(selSite);
    } catch (e) { toast(e.message, 'error'); }
  };

  // Search
  const doSearch = async (q) => {
    if (!q || q.length < 2) { setSearchResults(null); return; }
    setSearchLoading(true);
    try {
      const d = await api.get('/api/search?q=' + encodeURIComponent(q));
      setSearchResults(d);
    } catch { setSearchResults(null); }
    setSearchLoading(false);
  };

  const handleSearch = (q) => {
    setSearch(q);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => doSearch(q), 300);
  };

  const getSearchItems = useCallback(() => {
    if (!searchResults) return [];
    const items = [];
    (searchResults.sites || []).slice(0, 5).forEach(s => items.push({
      type: 'site', label: s.name, sub: s.address,
      go: () => { setSelSite(s.id); setView('sites'); setSearch(''); setSearchResults(null); window.history.pushState({ view: 'sites', siteId: s.id }, '', '/sites/' + s.id); }
    }));
    (searchResults.items || []).slice(0, 8).forEach(i => items.push({
      type: 'task', label: i.doc, sub: i.site_name + (i.ws_name ? ' · ' + i.ws_name : ''),
      go: () => { if (i.site_id) { setSelSite(i.site_id); setView('sites'); window.history.pushState({ view: 'sites', siteId: i.site_id }, '', '/sites/' + i.site_id); } setSearch(''); setSearchResults(null); }
    }));
    (searchResults.documents || []).slice(0, 5).forEach(d => items.push({
      type: 'doc', label: d.original_name || d.file_name, sub: d.site_name,
      go: () => { nav('documents'); setSearch(''); setSearchResults(null); }
    }));
    (searchResults.partners || []).slice(0, 5).forEach(p => items.push({
      type: 'partner', label: p.name, sub: p.role,
      go: () => { nav('partners'); setSearch(''); setSearchResults(null); }
    }));
    (searchResults.prospects || []).slice(0, 5).forEach(p => items.push({
      type: 'prospect', label: p.property_name, sub: p.address,
      go: () => { nav('pipeline'); setSearch(''); setSearchResults(null); }
    }));
    (searchResults.notes || []).slice(0, 5).forEach(n => items.push({
      type: 'note', label: n.text?.substring(0, 80), sub: n.author_name + ' in ' + (n.site_name || ''),
      go: () => { if (n.site_id) { setSelSite(n.site_id); setView('sites'); window.history.pushState({ view: 'sites', siteId: n.site_id }, '', '/sites/' + n.site_id); } setSearch(''); setSearchResults(null); }
    }));
    return items;
  }, [searchResults]);

  const handleSearchKey = (e) => {
    const items = getSearchItems();
    if (e.key === 'ArrowDown') { e.preventDefault(); setSearchIdx(i => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSearchIdx(i => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter' && searchIdx >= 0 && items[searchIdx]) { e.preventDefault(); items[searchIdx].go(); }
    else if (e.key === 'Escape') { setSearch(''); setSearchResults(null); setSearchIdx(-1); }
  };

  // Navigation
  const nav = (v) => {
    setView(v);
    setSelSite(null);
    setExpWS(new Set());
    setSiteData(null);
    setSearch('');
    setSearchResults(null);
    setTaskStatusFilter('');
    setTaskPriorityFilter('');
    if (mobile) setSb(false);
    const url = v === 'dashboard' ? '/' : '/' + v;
    window.history.pushState({ view: v }, '', url);
  };

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e) {
      const tag = (e.target.tagName || '').toLowerCase();
      const isInput = tag === 'input' || tag === 'textarea' || e.target.isContentEditable;
      const isMod = e.metaKey || e.ctrlKey;

      if (isMod && e.key === 'k') {
        e.preventDefault();
        if (searchInputRef.current) searchInputRef.current.focus();
        return;
      }
      if (isMod && e.key === 'n') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('dlp-shortcut-new', { detail: { view } }));
        return;
      }
      if (e.key === 'Escape') {
        if (showShortcuts) { setShowShortcuts(false); return; }
        if (chatOpen) { setChatOpen(false); return; }
        if (search) { setSearch(''); setSearchResults(null); return; }
        return;
      }
      if (isInput) return;
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setShowShortcuts(s => !s);
        return;
      }
      const navMap = { '1': 'dashboard', '2': 'pipeline', '3': 'calendar', '4': 'activity', '5': 'documents', '6': 'chat', '7': 'email', '8': 'partners' };
      if (navMap[e.key]) {
        e.preventDefault();
        if (e.key === '6') { setChatOpen(true); setUnread(0); if (mobile) setSb(false); }
        else nav(navMap[e.key]);
        return;
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [view, showShortcuts, chatOpen, search, mobile]);

  // Loading state
  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Dots /></div>;

  // Login screen
  if (!user) return <Login sessionExpired={sessionExpired} />;

  // Onboarding
  if (!user.onboarded) return <Onboard user={user} onDone={() => { api.get('/api/me').then(d => setUser(d.user)); }} />;

  const sStats = stats?.siteStats || [];
  const totals = stats?.totals || {};

  // Nav access: admins see everything, others filtered by nav_access JSONB
  const na = user.nav_access || {};
  const navAllowed = (id) => user.role === 'admin' || na[id] !== false;

  const navSections = [
    { label: 'MAIN', items: [
      { id: 'sites', icon: 'map-pin', l: 'Sites' },
      { id: 'dashboard', icon: 'layout-dashboard', l: 'Dashboard' },
      { id: 'pipeline', icon: 'target', l: 'Pipeline' },
    ].filter(n => navAllowed(n.id))},
    { label: 'MANAGE', items: [
      { id: 'calendar', icon: 'calendar', l: 'Calendar' },
      { id: 'partners', icon: 'users', l: 'Partners' },
      { id: 'accounting', icon: 'dollar-sign', l: 'Accounting' },
      { id: 'activity', icon: 'activity', l: 'Activity' },
      { id: 'documents', icon: 'folder-open', l: 'Documents' },
    ].filter(n => navAllowed(n.id))},
    { label: 'SYSTEM', items: [
      ...(user.role === 'admin' ? [{ id: 'admin', icon: 'settings', l: 'Admin' }] : []),
      { id: 'guide', icon: 'help-circle', l: 'Help' },
    ].filter(n => navAllowed(n.id))},
  ].filter(s => s.items.length > 0);
  const navItems = navSections.flatMap(s => s.items);

  const canEdit = user.role === 'admin' || user.role === 'member' || user.role === 'editor';
  const canAdmin = user.role === 'admin';
  const isViewer = user.role === 'viewer';

  return (
    <Ctx.Provider value={{ user, canEdit, canAdmin, isViewer }}>
      <div style={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden', background: T.bg }}>
        {/* Mobile overlay */}
        {mobile && sb && <div onClick={() => setSb(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 15 }} />}

        {/* Sidebar */}
        <div style={{
          width: sb ? (mobile ? 260 : 220) : 56,
          background: T.bgSidebar,
          borderRight: `1px solid ${T.border}`,
          display: 'flex', flexDirection: 'column', transition: 'width .3s',
          position: mobile ? 'fixed' : 'relative', zIndex: 20, flexShrink: 0,
          height: '100vh', left: mobile && !sb ? -260 : 0
        }}>
          <div style={{ padding: sb ? (mobile ? '10px 8px' : '12px 10px') : '16px 8px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Logo sz={sb ? (mobile ? 40 : 48) : 28} wide={sb} />
          </div>
          <div style={{ padding: mobile ? '8px 6px' : '14px 8px', flex: 1, overflow: 'auto' }}>
            {navSections.map((sec, si) => (
              <div key={sec.label}>
                {sb && <div style={{
                  fontSize: 10, fontWeight: 600, color: T.textMuted, letterSpacing: '0.12em',
                  textTransform: 'uppercase', padding: si === 0 ? '0 14px 6px' : '14px 14px 6px',
                  fontFamily: "'DM Sans', sans-serif"
                }}>{sec.label}</div>}
                {!sb && si > 0 && <div style={{ borderTop: `1px solid ${T.border}`, margin: '8px 8px' }} />}
                {sec.items.map(n => (
                  <button key={n.id} onClick={() => nav(n.id)} className={'nav-btn' + (view === n.id ? ' active' : '')} style={{
                    width: '100%', padding: sb ? (mobile ? '8px 14px' : '9px 14px') : '9px 0',
                    background: view === n.id ? T.bgHover : 'transparent',
                    border: '1px solid transparent',
                    borderLeft: view === n.id ? `2px solid ${T.accent}` : '2px solid transparent',
                    borderRadius: mobile ? 6 : 8, color: view === n.id ? T.text : T.textSecondary,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                    fontSize: mobile ? 13 : 14, fontWeight: view === n.id ? 500 : 400,
                    fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.01em',
                    marginBottom: mobile ? 1 : 2, justifyContent: sb ? 'flex-start' : 'center'
                  }}>
                    <span style={{ fontSize: 14, opacity: view === n.id ? 1 : 0.7 }}>{renderIcon(n.icon, 17, view === n.id ? T.accent : T.textMuted)}</span>
                    {sb && n.l}
                  </button>
                ))}
              </div>
            ))}
            {/* Chat button */}
            {navAllowed('chat') && <button onClick={() => { setChatOpen(true); setUnread(0); if (mobile) setSb(false); }} className="nav-btn" style={{
              width: '100%', padding: sb ? (mobile ? '8px 14px' : '9px 14px') : '9px 0',
              background: chatOpen ? T.bgHover : 'transparent',
              border: '1px solid transparent', borderRadius: mobile ? 6 : 8,
              color: chatOpen ? T.text : T.textSecondary, cursor: 'pointer', display: 'flex',
              alignItems: 'center', gap: 10, fontSize: mobile ? 13 : 14,
              fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.01em',
              marginBottom: mobile ? 1 : 2, justifyContent: sb ? 'flex-start' : 'center',
              position: 'relative'
            }}>
              <span style={{ fontSize: 14, opacity: chatOpen ? 1 : 0.7 }}>{renderIcon('message-circle', 17, chatOpen ? T.accent : T.textMuted)}</span>
              {sb && 'Chat'}
              {unread > 0 && <span style={{ background: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '2px 7px', marginLeft: 'auto' }}>{unread}</span>}
            </button>}
          </div>
          <div style={{ padding: mobile ? '8px 10px' : '12px', borderTop: `1px solid ${T.border}` }}>
            {sb && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: mobile ? 4 : 8 }}>
              <Avatar name={user.name} color={user.avatar_color} sz={mobile ? 24 : 28} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: mobile ? 12 : 13, fontWeight: 500, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'DM Sans', sans-serif" }}>{user.name}</div>
                <div style={{ fontSize: 10, color: T.textMuted, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em' }}>
                  {user.role}
                  {(user.role === 'partner' || user.role === 'viewer') && <span style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B', padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 600, letterSpacing: '0.06em' }}>READ ONLY</span>}
                  {user.role === 'editor' && <span style={{ background: 'rgba(96,165,250,0.12)', color: '#4ADE80', padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 600, letterSpacing: '0.06em' }}>EDITOR</span>}
                </div>
              </div>
            </div>}
            <div style={{ display: 'flex', gap: 6, marginBottom: 0 }}>
              <button onClick={toggleTheme} className="action-btn" style={{ flex: sb ? 'none' : 1, padding: mobile ? '5px 8px' : '7px 10px', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`, borderRadius: 6, color: isDark ? 'rgba(255,255,255,0.42)' : 'rgba(0,0,0,0.45)', fontSize: 11, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, letterSpacing: '0.04em', cursor: 'pointer' }}>
                {isDark ? (sb ? '☀ Light' : '☀') : (sb ? '☾ Dark' : '☾')}
              </button>
              <button onClick={logout} className="action-btn" style={{ flex: 1, padding: mobile ? '5px' : '7px', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`, borderRadius: 6, color: isDark ? 'rgba(255,255,255,0.42)' : 'rgba(0,0,0,0.45)', fontSize: 11, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, letterSpacing: '0.04em' }}>
                {sb ? 'Sign out' : '⏻'}
              </button>
            </div>
          </div>
          {!mobile && <button onClick={() => setSb(!sb)} className="action-btn" style={{
            padding: 8, background: 'rgba(255,255,255,0.02)', border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)',
            color: 'rgba(255,255,255,0.32)', fontSize: 12, fontWeight: 500, width: '100%', fontFamily: "'DM Sans', sans-serif"
          }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'} onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}>
            {sb ? '◀ Collapse' : '▶'}
          </button>}
        </div>

        {/* Main content */}
        <div className={mobile ? 'mobile-main-padded' : ''} style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          {/* Top bar */}
          <div style={{
            padding: mobile ? 'calc(10px + env(safe-area-inset-top, 0px)) 16px 10px' : '14px 28px',
            borderBottom: `1px solid ${T.border}`,
            background: isDark ? 'rgba(22,22,24,0.92)' : 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
            position: 'sticky', top: 0, zIndex: 5,
            display: 'flex', alignItems: 'center', gap: 12
          }}>
            {mobile && <button onClick={() => setSb(true)} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 18, padding: 4 }}>☰</button>}
            <div style={{ position: 'relative', flex: 1, maxWidth: mobile ? undefined : 320 }}>
              <input
                ref={searchInputRef}
                placeholder="Search items, docs, notes..."
                value={search}
                onChange={e => { handleSearch(e.target.value); setSearchIdx(-1); }}
                onKeyDown={handleSearchKey}
                style={{ width: '100%', padding: '10px 14px 10px 34px', background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 14, fontFamily: "'DM Sans', sans-serif", outline: 'none', transition: 'border-color 150ms cubic-bezier(0.16,1,0.3,1), box-shadow 200ms cubic-bezier(0.16,1,0.3,1)' }}
              />
              {!mobile && <span className="kbd-hint" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>⌘K</span>}
              <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: T.textMuted, fontSize: 13 }}>⌕</span>
            </div>
            <AutoRefreshIndicator lastUpdated={dashboardAR.lastUpdated} isRefreshing={dashboardAR.isRefreshing} onRefresh={dashboardAR.refresh} dataChanged={dashboardAR.dataChanged} compact={mobile} />
            <NotificationBell onNav={nav} />
          </div>

          {/* Smart Search Dropdown */}
          {searchResults && search.length >= 2 && (() => {
            const items = getSearchItems();
            const typeIcons = { site: 'map-pin', task: 'clipboard-list', doc: 'file', partner: 'users', prospect: 'search', note: 'file-text' };
            const typeColors = { site: '#F59E0B', task: '#4ADE80', doc: theme.accent, partner: T.textSecondary, prospect: '#F59E0B', note: '#4ADE80' };
            const typeLabels = { site: 'Sites', task: 'Tasks', doc: 'Documents', partner: 'Partners', prospect: 'Prospects', note: 'Notes' };
            let globalIdx = -1;
            const grouped = {};
            items.forEach(it => { if (!grouped[it.type]) grouped[it.type] = []; grouped[it.type].push(it); });
            return (
              <div style={{ position: 'absolute', left: mobile ? 16 : 28, right: mobile ? 16 : undefined, width: mobile ? undefined : 400, top: 56, background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: T.shadowLg, zIndex: 50, overflow: 'hidden', maxHeight: 420 }}>
                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: T.textSecondary }}>{items.length} results</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: T.textMuted }}>↑↓ navigate · Enter select · Esc close</span>
                    <button onClick={() => { setSearch(''); setSearchResults(null); setSearchIdx(-1); }} style={{ background: 'none', border: 'none', color: T.textSecondary, cursor: 'pointer', fontSize: 16 }}>×</button>
                  </div>
                </div>
                <div style={{ overflow: 'auto', maxHeight: 360 }}>
                  {items.length === 0 && <EmptyState icon="search" title={'No results for "' + search + '"'} description="Try adjusting your search terms" />}
                  {Object.keys(grouped).map(type => (
                    <div key={type}>
                      <div style={{ padding: '8px 14px', fontSize: 11, fontWeight: 700, color: typeColors[type], textTransform: 'uppercase', letterSpacing: 1, background: T.bgHover }}>
                        {renderIcon(typeIcons[type], 13)} {typeLabels[type]} ({grouped[type].length})
                      </div>
                      {grouped[type].map(item => {
                        globalIdx++;
                        const idx = globalIdx;
                        return (
                          <div key={type + '-' + idx} onClick={item.go} style={{
                            padding: '8px 14px', cursor: 'pointer',
                            background: searchIdx === idx ? T.bgHover : 'transparent',
                            borderBottom: `1px solid ${T.borderLight}`, transition: 'background .1s'
                          }} onMouseEnter={() => setSearchIdx(idx)}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: searchIdx === idx ? T.text : T.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</div>
                            {item.sub && <div style={{ fontSize: 11, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.sub}</div>}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <div style={{ padding: mobile ? '20px 16px' : '24px 28px' }} className="fi">
            <PageTransition viewKey={view + (selSite || '')}>
              <Suspense fallback={<SuspenseFallback />}>

                {/* DASHBOARD */}
                {view === 'dashboard' && <DashboardView
                  stats={stats} totals={totals} sStats={sStats} overdueCount={overdueCount}
                  activity={activity} users={users} sites={sites} user={user} mobile={mobile}
                  nav={nav} setSelSite={setSelSite} setView={setView} setExpWS={setExpWS}
                  theme={theme} isViewer={isViewer} isDark={isDark} R={R} N={N}
                  refresh={refresh}
                />}

                {/* PIPELINE */}
                {view === 'pipeline' && <PipelineView onNavigate={(v, id) => {
                  if (v === 'sites' && id) { setSelSite(id); setView('sites'); window.history.pushState({ view: 'sites', siteId: id }, '', '/sites/' + id); }
                  else { nav(v); }
                }} />}

                {/* SITES LIST */}
                {view === 'sites' && !selSite && <SiteMissionControl sites={sites} sStats={sStats} onSelect={(id) => {
                  setSelSite(id); setExpWS(new Set()); setWfStage(null);
                  window.history.pushState({ view: 'sites', siteId: id }, '', '/sites/' + id);
                }} users={users} activity={activity} canAdmin={canAdmin} onReorder={refresh} />}

                {/* SITE DETAIL */}
                {view === 'sites' && selSite && siteData && <SiteDetailView
                  siteData={siteData} selSite={selSite} setSelSite={setSelSite} setExpWS={setExpWS}
                  setWfStage={setWfStage} setSiteData={setSiteData} selItems={selItems} setSelItems={setSelItems}
                  user={user} users={users} partners={partners} canAdmin={canAdmin} canEdit={canEdit} isViewer={isViewer}
                  mobile={mobile} isDark={isDark} theme={theme} T={T} R={R} N={N}
                  updateItem={updateItem} deleteItem={deleteItem} addNote={addNote} loadSite={loadSite} refresh={refresh}
                  showConfirm={showConfirm} nav={nav} api={api}
                  selectedPhases={selectedPhases} setSelectedPhases={setSelectedPhases}
                />}

                {/* PARTNERS */}
                {view === 'partners' && <PartnersView />}

                {/* ACCOUNTING */}
                {view === 'accounting' && <AccountingView user={user} />}

                {/* ACTIVITY */}
                {view === 'activity' && <ActivityLog />}

                {/* GUIDE / HELP */}
                {view === 'guide' && (user.role === 'admin' ? <AdminGuide /> : <MemberGuide />)}

                {/* CALENDAR */}
                {view === 'calendar' && <CalendarView userId={user.id} hasGoogle={user.hasGoogle} />}

                {/* EMAIL + MEET */}
                {view === 'email' && <div>
                  {user.hasGoogle && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <button onClick={async () => {
                      const ok = await showConfirm({ title: 'Disconnect Google?', message: "You'll need to reconnect to use Gmail, Calendar, Drive, and Meet.", danger: true });
                      if (!ok) return;
                      try { await api.post('/api/google/disconnect'); window.location.reload(); } catch (e) { alert(e.message); }
                    }} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 12, textDecoration: 'underline', padding: 0 }}>Disconnect Google</button>
                  </div>}
                  {isViewer && <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#f59e0b', fontWeight: 500 }}>
                    {renderIcon('lock', 14)} <span>You have <strong>read-only</strong> access to email.</span>
                  </div>}
                  <GmailPanel userId={user.id} hasGoogle={user.hasGoogle} />
                  <div style={{ height: 20 }} />
                  <MeetPanel userId={user.id} hasGoogle={user.hasGoogle} sites={sites} />
                </div>}

                {/* DOCUMENTS */}
                {view === 'documents' && <DocumentsBrowser user={user} />}

                {/* ANALYTICS */}
                {view === 'analytics' && <AnalyticsDashboard />}

                {/* ADMIN */}
                {view === 'admin' && user.role === 'admin' && <AdminPortal />}

              </Suspense>
            </PageTransition>
          </div>
        </div>

        {/* Chat overlay */}
        <Chat open={chatOpen} onClose={() => setChatOpen(false)} sites={sites} />

        {/* Command palette */}
        <CmdPalette onNav={(v, id) => {
          if (v === 'sites' && id) { setSelSite(id); setView('sites'); window.history.pushState({ view: 'sites', siteId: id }, '', '/sites/' + id); }
          else { nav(v); }
        }} />

        {/* Keyboard shortcuts modal */}
        {showShortcuts && (
          <div onClick={() => setShowShortcuts(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
            <div onClick={e => e.stopPropagation()} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, boxShadow: T.shadowLg, maxHeight: '80vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>⌨️ Keyboard Shortcuts</h2>
                <button onClick={() => setShowShortcuts(false)} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 20, padding: 4 }}>✕</button>
              </div>
              {[
                { title: 'Navigation', items: [['1-8', 'Quick navigate pages'], ['?', 'Show this help']] },
                { title: 'Actions', items: [['⌘/Ctrl + K', 'Focus search bar'], ['⌘/Ctrl + N', 'Add new (context-dependent)'], ['Escape', 'Close modal / clear search']] },
                { title: 'Page Map', items: [['1', 'Dashboard'], ['2', 'Pipeline'], ['3', 'Calendar'], ['4', 'Activity'], ['5', 'Documents'], ['6', 'Chat'], ['7', 'Email'], ['8', 'Partners']] },
              ].map(section => (
                <div key={section.title} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>{section.title}</div>
                  {section.items.map(([key, desc]) => (
                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                      <span style={{ color: T.textSecondary, fontSize: 14 }}>{desc}</span>
                      <kbd style={{ fontFamily: "'SF Mono','Consolas',monospace", fontSize: 12, padding: '3px 8px', borderRadius: 5, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', border: `1px solid ${T.border}`, color: T.text, fontWeight: 500 }}>{key}</kbd>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mobile bottom nav */}
        {mobile && <BottomNav view={view} onNav={nav} chatOpen={chatOpen} onChatOpen={() => { setChatOpen(true); setUnread(0); }} unread={unread} isAdmin={user.role === 'admin'} />}

        {/* PWA prompts */}
        <PWAInstallPrompt />
        <UpdateToast />
      </div>
    </Ctx.Provider>
  );
}